import { useState, useEffect, useRef, useCallback, Dispatch } from "react";
import useEventListener from "@use-it/event-listener";

const globalState: { [key: string]: any } = {};

const createGlobalState = <T>(
  key: string,
  thisCallback: Dispatch<T>,
  initialValue: T
) => {
  if (!globalState[key]) {
    globalState[key] = { callbacks: [], value: initialValue };
  }
  globalState[key].callbacks.push(thisCallback);
  return {
    deregister() {
      const arr = globalState[key].callbacks;
      const index = arr.indexOf(thisCallback);
      if (index > -1) {
        arr.splice(index, 1);
      }
    },
    emit(value: T) {
      if (globalState[key].value !== value) {
        globalState[key].value = value;
        globalState[key].callbacks.forEach((callback: Dispatch<T>) => {
          if (thisCallback !== callback) {
            callback(value);
          }
        });
      }
    },
  };
};

const usePersistedState: <T>(
  initialState: T,
  key: string,
  get_set: {
    get: (key: string, initialValue: T) => T;
    set: (key: string, newValue: T) => void;
  }
) => [T, Dispatch<T>] = <T>(
  initialState: T,
  key: string,
  {
    get,
    set,
  }: {
    get: (key: string, initialValue: T) => T;
    set: (key: string, newValue: T) => void;
  }
) => {
  const globalState = useRef<{
    deregister: () => void;
    emit: Dispatch<T>;
  } | null>(null);
  const [state, setState] = useState(() => get(key, initialState));

  // subscribe to `storage` change events
  useEventListener("storage", ({ key: k, newValue }: any) => {
    if (k === key) {
      const newState = JSON.parse(newValue);
      if (state !== newState) {
        setState(newState);
      }
    }
  });

  // only called on mount
  useEffect(() => {
    // register a listener that calls `setState` when another instance emits
    globalState.current = createGlobalState(key, setState, state);

    return () => {
      globalState.current?.deregister();
    };
  }, [key]);

  const persistentSetState = useCallback(
    (newState) => {
      const newStateValue =
        typeof newState === "function" ? newState(state) : newState;

      // persist to localStorage
      set(key, newStateValue);

      setState(newStateValue);

      // inform all of the other instances in this tab
      globalState.current?.emit(newState);
    },
    [state, set, key]
  );

  return [state, persistentSetState];
};

const createStorage = <T>(provider: Storage) => ({
  get(key: string, defaultValue: T) {
    const json = provider.getItem(key);
    // eslint-disable-next-line no-nested-ternary
    return json === null || typeof json === "undefined"
      ? typeof defaultValue === "function"
        ? defaultValue()
        : defaultValue
      : JSON.parse(json);
  },
  set(key: string, value: T) {
    provider.setItem(key, JSON.stringify(value));
  },
});

const getProvider = () => {
  if (typeof global !== "undefined" && global.localStorage) {
    return global.localStorage;
  }
  // eslint-disable-next-line no-undef
  if (typeof globalThis !== "undefined" && globalThis.localStorage) {
    // eslint-disable-next-line no-undef
    return globalThis.localStorage;
  }
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  if (typeof localStorage !== "undefined") {
    return localStorage;
  }
  return null;
};

const createPersistedState: <T>(
  key: string,
  provider?: Storage
) => (initialState: T) => [T, Dispatch<T>] = <T>(
  key: string,
  provider = getProvider()
) => {
  if (provider) {
    const storage = createStorage(provider);
    return (initialState: T) => usePersistedState(initialState, key, storage);
  }
  return useState;
};

export default createPersistedState;
